import { useContext, useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { lineString, polygon } from "@turf/turf";
import lineIntersect from "@turf/line-intersect";
import length from "@turf/length";
import along from "@turf/along";
import { Units } from "@turf/helpers";
import { LeafletEventHandlerFnMap, LatLng } from "leaflet";
import { useAsset } from "../useAsset";
import { StickyNote } from "../../../model/viewModel/stickyNote";
import { ArrowAnnotationType } from "../../../model/internal/annotation";
import { useDispatch } from "react-redux";
import { ToolContext } from "../../../context/ToolContext";
import Arrow from "./Arrow";
import { Marker, useMap } from "react-leaflet";
import { updateArrowAnnotationDirectly } from "../../../app/networkSlice";
import { stickyNoteIconMarker } from "../../StickyNoteIconMarker";
import { ZIndex } from "../../../constants/zindex";
import L from "leaflet";
import { getAllArrowSnapTargetAssets } from "../../../app/networkSlice";

interface ArrowAnnotationProps {
  arrow: ArrowAnnotationType;
  highlighted: boolean;
  easyClickTolerance: number;
  eventHandlers?: LeafletEventHandlerFnMap;
}

const grabHandleSize = 16;

// This mimics the custom handler icon used in ResizeAndTranslateTool
const circleGrabHandleIcon = L.divIcon({
  className: "circle-arrow-handler-marker",
  iconSize: [grabHandleSize, grabHandleSize],
  html: `
    <div style="
      width:${grabHandleSize}px;
      height:${grabHandleSize}px;
      border-radius:50%;
      background:#fff;
      border:2px solid #3388ff;
      opacity:1;
      box-sizing:border-box;
      box-shadow:0 0 0 2px #3388ff33;
    "/>
  `,
});

const squareGrabHandleIcon = L.divIcon({
  className: "square-arrow-handler-marker",
  iconSize: [grabHandleSize, grabHandleSize],
  html: `
    <div style="
      width:${grabHandleSize}px;
      height:${grabHandleSize}px;
      background:#fff;
      border:2px solid #3388ff;
      opacity:1;
      box-sizing:border-box;
      box-shadow:0 0 0 2px #3388ff33;
      cursor:move;
    "/>
  `,
});

const ArrowAnnotation = ({
  arrow,
  highlighted,
  easyClickTolerance,
  eventHandlers,
}: ArrowAnnotationProps) => {
  const { isAssetSelected, isAssetRingfenced } = useAsset();
  const { toolState, setToolState } = useContext(ToolContext);
  const dispatch = useDispatch();
  const map = useMap();
  const centerRef = useRef<LatLng | null>(null);
  const [isDraggingCenter, setIsDraggingCenter] = useState<boolean>(false);
  const [isDraggingVertex, setIsDraggingVertex] = useState<boolean>(false);
  const [draggingGeometry, setDraggingGeometry] = useState<LatLng[]>([]);
  const { snapTargetAssetId } = toolState;
  const allTargetAssets = useSelector((state: any) => getAllArrowSnapTargetAssets(state));

  const isSelected = isAssetSelected(arrow.id);
  const isRingfenced = isAssetRingfenced(arrow.id);

  const setSnapExcludedAssetId = (assetId: string) => {
    const _toolState = {
      ...toolState,
      snapExcludedAssetId: assetId,
      snapTargetAssetId: "",
    };
    setToolState(_toolState);
  };

  const setDraggingArrowVertex = (isDragging: boolean): void => {
    const _toolState = toolState;
    _toolState.draggingArrowVertex = isDragging;
    setToolState(_toolState);
  };

  const getCenter = (coords: L.Point[]) => {
    return new L.Point((coords[0].x + coords[1].x) / 2, (coords[0].y + coords[1].y) / 2);
  };

  const getGeoCenter = (coords: LatLng[]) => {
    const vertices = coords.map((g) => map.latLngToContainerPoint(g));
    const center = getCenter(vertices);
    const geocenter = map.containerPointToLatLng(center);
    return geocenter;
  };

  const updateCenterRef = (arrowGeometry: LatLng[]) => {
    centerRef.current = getGeoCenter(arrowGeometry);
  };

  const measureStickyNote = (stickNote: StickyNote): { width: number; height: number } => {
    const icon = stickyNoteIconMarker(stickNote, false);
    const marker = L.marker(stickNote.geometry, {
      icon: icon,
    }).addTo(map);
    const el = marker.getElement() as any;
    const elSticky = el.querySelector(".sticky-note-icon-marker");
    const { width, height } = elSticky.getBoundingClientRect();
    marker.remove();
    return { width, height };
  };

  const getCenterOfAsset = (asset: any): LatLng | null => {
    if (!asset || !asset.geometry) {
      return null;
    }
    if (asset.styles.class === "circle") {
      return asset.geometry.center;
    }
    if (asset.styles.class === "rectangle") {
      const latitudes = asset.geometry.map((pt: LatLng) => pt.lat);
      const longitudes = asset.geometry.map((pt: LatLng) => pt.lng);
      const north = Math.max(...latitudes);
      const south = Math.min(...latitudes);
      const east = Math.max(...longitudes);
      const west = Math.min(...longitudes);
      const centerLat = (north + south) / 2;
      const centerLng = (east + west) / 2;
      return new L.LatLng(centerLat, centerLng);
    }
    if (asset.styles.class === "sticky-note") {
      const { width, height } = measureStickyNote(asset);
      const topLeft = map.latLngToContainerPoint(asset.geometry);
      const center = new L.Point(topLeft.x + width / 2, topLeft.y + height / 2);
      const geoCenter = map.containerPointToLatLng(center);
      return geoCenter;
    }
    return null;
  };

  const trimToAssetBoundary = (asset: any, geometry: LatLng, otherEndGeometry: LatLng): LatLng => {
    const geoLine = lineString([geometry, otherEndGeometry].map((a) => [a.lng, a.lat]));
    let intersectionPoint = map.latLngToContainerPoint(geometry);

    if (!asset || !asset.geometry) {
      return geometry;
    }

    if (asset.styles.class === "circle") {
      const options = { units: "meters" as Units };
      const geoLineLength = length(geoLine, options);
      // Only trim the line if the end is outside the circle
      if (geoLineLength > asset.geometry.radius) {
        const turfIntersectionPoint = along(geoLine, asset.geometry.radius, options);
        const geoItersectionPoint = new LatLng(
          turfIntersectionPoint.geometry.coordinates[1],
          turfIntersectionPoint.geometry.coordinates[0],
        );
        intersectionPoint = map.latLngToContainerPoint(geoItersectionPoint);
      }
    }
    if (asset.styles.class === "rectangle") {
      const assetPolygon = polygon([
        [
          ...asset.geometry.map((a: LatLng) => [a.lng, a.lat]),
          [asset.geometry[0].lng, asset.geometry[0].lat],
        ],
      ]);
      const turfIntersectionPoint = lineIntersect(assetPolygon, geoLine);
      if (turfIntersectionPoint.features.length > 0) {
        const geoIntersectionPoint = new LatLng(
          turfIntersectionPoint.features[0].geometry.coordinates[1],
          turfIntersectionPoint.features[0].geometry.coordinates[0],
        );
        intersectionPoint = map.latLngToContainerPoint(geoIntersectionPoint);
      }
    }
    if (asset.styles.class === "sticky-note") {
      const { width, height } = measureStickyNote(asset);
      const topLeft = map.latLngToContainerPoint(asset.geometry);
      const topRight = new L.Point(topLeft.x + width, topLeft.y);
      const bottomRight = new L.Point(topLeft.x + width, topLeft.y + height);
      const bottomLeft = new L.Point(topLeft.x, topLeft.y + height);
      const coords = [topLeft, topRight, bottomRight, bottomLeft, topLeft];
      const latLngs = coords.map((pt) => map.containerPointToLatLng(pt));

      const assetPolygon = polygon([latLngs.map((a: LatLng) => [a.lng, a.lat])]);
      const turfIntersectionPoint = lineIntersect(assetPolygon, geoLine);
      if (turfIntersectionPoint.features.length > 0) {
        const geoIntersectionPoint = new LatLng(
          turfIntersectionPoint.features[0].geometry.coordinates[1],
          turfIntersectionPoint.features[0].geometry.coordinates[0],
        );
        intersectionPoint = map.latLngToContainerPoint(geoIntersectionPoint);
      }
    }

    return map.containerPointToLatLng(intersectionPoint);
  };

  const calculateSnappedGeometry = (geometry: LatLng[], snap: string[] | undefined | null) => {
    if (!snap || snap.length !== 2) {
      return geometry;
    }

    const startAsset =
      !!snap[0] && snap[0] !== snap[1]
        ? allTargetAssets.find((asset) => asset.id === snap[0])
        : null;
    const endAsset = !!snap[1] ? allTargetAssets.find((asset) => asset.id === snap[1]) : null;

    // First get a line which is the correct direction but too long
    const startGeometry = getCenterOfAsset(startAsset) ?? geometry[0];
    const endGeometry = getCenterOfAsset(endAsset) ?? geometry[1];

    // Now trim the line to the edge of the assets
    const trimmedStart = trimToAssetBoundary(startAsset, startGeometry, endGeometry);
    const trimmedEnd = trimToAssetBoundary(endAsset, endGeometry, startGeometry);

    return [trimmedStart, trimmedEnd];
  };

  useEffect(() => {
    if (!!arrow.snap && arrow.snap.length) {
      // Update the snap array to remove any targets that no longer exist
      const newSnap = [
        allTargetAssets.some((asset) => asset.id === arrow.snap?.[0]) ? arrow.snap[0] : "",
        allTargetAssets.some((asset) => asset.id === arrow.snap?.[1]) ? arrow.snap[1] : "",
      ];
      if (newSnap[0] !== arrow.snap?.[0] || newSnap[1] !== arrow.snap?.[1]) {
        dispatch(
          updateArrowAnnotationDirectly({
            ...arrow,
            snap: newSnap,
          }),
        );
      }
    }
    const newGeometry = calculateSnappedGeometry(arrow.geometry, arrow.snap);
    if (
      newGeometry[0].lat !== arrow.geometry[0].lat ||
      newGeometry[0].lng !== arrow.geometry[0].lng ||
      newGeometry[1].lat !== arrow.geometry[1].lat ||
      newGeometry[1].lng !== arrow.geometry[1].lng
    ) {
      dispatch(
        updateArrowAnnotationDirectly({
          ...arrow,
          geometry: newGeometry,
        }),
      );
    }
  });

  useEffect(() => {
    updateCenterRef(arrow.geometry);
  }, [arrow.geometry]);

  // Dragend is not always fired, eg when user presses escape while dragging
  // Hence clear dragging state using mouseup as fallback
  useEffect(() => {
    const handleMouseUp = () => {
      if (draggingGeometry.length > 0) {
        setIsDraggingCenter(false);
        setIsDraggingVertex(false);
        setDraggingGeometry([]);
        setDraggingArrowVertex(false);
      }
    };

    if (draggingGeometry.length > 0) {
      window.addEventListener("mouseup", handleMouseUp);
      return () => window.removeEventListener("mouseup", handleMouseUp);
    }
  }, [draggingGeometry]);

  const getArrowGeometryDraggedByCenter = (newCenter: LatLng) => {
    const startingVertices = arrow.geometry.map((g: LatLng) => map.latLngToContainerPoint(g));
    const startingCenter = getCenter(startingVertices);
    const movedCenter = map.latLngToContainerPoint(newCenter);
    const xOffset = movedCenter.x - startingCenter.x;
    const yOffset = movedCenter.y - startingCenter.y;
    const movedVertices = startingVertices.map(
      (vertex: L.Point) => new L.Point(vertex.x + xOffset, vertex.y + yOffset),
    );
    const movedGeometry = movedVertices.map((v: L.Point) => map.containerPointToLatLng(v));
    return movedGeometry;
  };

  const getArrowGeometryDraggedByVertex = (index: number, latlng: LatLng) => {
    const newGeometry = [...arrow.geometry];
    newGeometry[index] = latlng;
    return newGeometry;
  };

  const handleDragEnd = (newGeometry: LatLng[], newSnap?: string[]) => {
    dispatch(
      updateArrowAnnotationDirectly({
        ...arrow,
        geometry: newGeometry,
        snap: newSnap ?? arrow.snap,
      }),
    );
    updateCenterRef(newGeometry);
  };

  const handleDragEndCenter = (dragEndCoord: LatLng) =>
    handleDragEnd(getArrowGeometryDraggedByCenter(dragEndCoord));

  const getSnap = (arrowVertex: number, snapTargetAssetId: string): string[] | undefined => {
    const snap0 = arrowVertex === 0 ? snapTargetAssetId ?? "" : arrow.snap?.[0] ?? "";
    const snap1 = arrowVertex === 1 ? snapTargetAssetId ?? "" : arrow.snap?.[1] ?? "";
    const newSnap = [snap0, snap1];
    return newSnap;
  };

  const handleDragEndVertex = (index: number, latlng: LatLng) => {
    const snap = getSnap(index, snapTargetAssetId as string);
    handleDragEnd(getArrowGeometryDraggedByVertex(index, latlng), snap);
  };

  const isSnapped = (): boolean => {
    return !!arrow.snap && arrow.snap.length >= 2 && (!!arrow.snap[0] || !!arrow.snap[1]);
  };
  const isDragging = () => isDraggingCenter || isDraggingVertex;

  return (
    <>
      {!isDragging() && (
        <Arrow
          id={arrow.id}
          positions={arrow.geometry}
          eventHandlers={eventHandlers}
          isHighlighted={highlighted || isRingfenced}
          color={arrow.styles.color}
          opacity={isRingfenced ? 0.5 : 1}
          zoomLevel={map.getZoom()}
          easyClickTolerance={easyClickTolerance}
          interactive={true}
        />
      )}
      {isDragging() && (
        <Arrow
          id={`${arrow.id}-dragging`}
          positions={draggingGeometry}
          isHighlighted={true}
          color={arrow.styles.color}
          opacity={0.5}
          zoomLevel={map.getZoom()}
          easyClickTolerance={easyClickTolerance}
          interactive={false}
        />
      )}
      {!isSnapped() && !isDraggingVertex && isSelected && !!centerRef.current && (
        <Marker
          zIndexOffset={ZIndex.shapeGrabHandles}
          key={`arrow-node-${arrow.id}-center`}
          position={centerRef.current}
          draggable={true}
          eventHandlers={{
            drag: (e) => {
              setIsDraggingCenter(true);
              setDraggingGeometry(getArrowGeometryDraggedByCenter(e.target.getLatLng()));
            },
            dragend: (e) => {
              setIsDraggingCenter(false);
              setIsDraggingVertex(false);
              setDraggingGeometry([]);
              handleDragEndCenter(e.target.getLatLng());
            },
          }}
          icon={squareGrabHandleIcon}
        />
      )}
      {!isDraggingCenter &&
        isSelected &&
        arrow.geometry.map((pos: LatLng, index: number) => (
          <Marker
            zIndexOffset={ZIndex.shapeGrabHandles}
            key={`arrow-node-${arrow.id}-${index}`}
            position={pos}
            draggable={true}
            eventHandlers={{
              dragstart: (e) => {
                // Prevent the marker from capturing pointer events during drag
                e.target.getElement().style.pointerEvents = "none";
                setDraggingArrowVertex(true);
                const indexOfOtherEnd = index === 0 ? 1 : 0;
                const assetIdSnappedToOtherEnd = arrow.snap?.[indexOfOtherEnd] ?? "";
                setSnapExcludedAssetId(assetIdSnappedToOtherEnd);
              },
              drag: (e) => {
                setIsDraggingVertex(true);
                setDraggingGeometry(getArrowGeometryDraggedByVertex(index, e.target.getLatLng()));
              },
              dragend: (e) => {
                e.target.getElement().style.pointerEvents = "";
                setIsDraggingCenter(false);
                setIsDraggingVertex(false);
                setDraggingGeometry([]);
                handleDragEndVertex(index, e.target.getLatLng());
                setDraggingArrowVertex(false);
                setSnapExcludedAssetId("");
              },
            }}
            icon={circleGrabHandleIcon}
          />
        ))}
    </>
  );
};

export default ArrowAnnotation;
